import React, { useState } from 'react';
import {
  CheckCircle, XCircle, Edit2, ExternalLink, ChevronDown, ChevronUp,
  Mail, Phone, Linkedin, Globe, Tag, Zap, AlertTriangle, Search, Loader2
} from 'lucide-react';
import { enrichContact } from '../services/prospectingService';
import type { Prospect } from '../services/prospectingService';

interface Props {
  prospect: Prospect;
  isApproved: boolean;
  isRejected: boolean;
  onApprove: () => void;
  onReject: () => void;
  onEdit: () => void;
  onEnrich?: (updated: Partial<Prospect>) => void;
}

const PAIN_COLORS: Record<string, string> = {
  VISIBILITY:  'bg-blue-100 text-blue-700 dark:bg-blue-950/40 dark:text-blue-400',
  EXECUTION:   'bg-orange-100 text-orange-700 dark:bg-orange-950/40 dark:text-orange-400',
  DATA:        'bg-purple-100 text-purple-700 dark:bg-purple-950/40 dark:text-purple-400',
  QUALITY:     'bg-rose-100 text-rose-700 dark:bg-rose-950/40 dark:text-rose-400',
  INTEGRATION: 'bg-teal-100 text-teal-700 dark:bg-teal-950/40 dark:text-teal-400',
};

const MATURITY_COLORS: Record<string, string> = {
  L1: 'bg-red-100 text-red-700 dark:bg-red-950/40 dark:text-red-400',
  L2: 'bg-orange-100 text-orange-700 dark:bg-orange-950/40 dark:text-orange-400',
  L3: 'bg-yellow-100 text-yellow-700 dark:bg-yellow-950/40 dark:text-yellow-400',
  L4: 'bg-emerald-100 text-emerald-700 dark:bg-emerald-950/40 dark:text-emerald-400',
  L5: 'bg-blue-100 text-blue-700 dark:bg-blue-950/40 dark:text-blue-400',
};

const icpGrade = (score: number | null) => {
  if (score === null) return { grade: '?', cls: 'bg-muted text-muted-foreground' };
  if (score >= 80) return { grade: 'A', cls: 'bg-emerald-100 text-emerald-700 dark:bg-emerald-950/40 dark:text-emerald-400' };
  if (score >= 60) return { grade: 'B', cls: 'bg-blue-100 text-blue-700 dark:bg-blue-950/40 dark:text-blue-400' };
  if (score >= 40) return { grade: 'C', cls: 'bg-yellow-100 text-yellow-700 dark:bg-yellow-950/40 dark:text-yellow-400' };
  return { grade: 'D', cls: 'bg-red-100 text-red-700 dark:bg-red-950/40 dark:text-red-400' };
};

export default function ProspectCard({ prospect: p, isApproved, isRejected, onApprove, onReject, onEdit, onEnrich }: Props) {
  const [outreachOpen, setOutreachOpen] = useState(false);
  const [sourcesOpen, setSourcesOpen]   = useState(false);
  const [outreachTab, setOutreachTab]   = useState<'email' | 'call' | 'linkedin'>('email');

  const { grade, cls: gradeClass } = icpGrade(p.icpScoreEstimate);

  // Effective state — staged takes priority over DB status
  const effectiveApproved = isApproved || p.reviewStatus === 'APPROVED';
  const effectiveRejected = isRejected || p.reviewStatus === 'REJECTED';
  // Approval requires a verified contact email — no assumed/blank emails allowed
  const hasVerifiedEmail = !!(p.keyContactName && p.keyContactLinkedIn) ||
                           !!(p.keyContactName && p.keyContactTitle) ||
                           false; // will be true when email connector returns real email
  const canApprove = !effectiveApproved && !effectiveRejected;
  // Block only when there is truly no contact information at all
  const needsEmail = !p.keyContactName && !p.keyContactEmail && !p.keyContactLinkedIn;
  // Warn (but don't block) when contact name exists but email is missing
  const missingEmail = !needsEmail && !p.keyContactEmail;
  const isDone = effectiveApproved || effectiveRejected;
  const [enriching, setEnriching] = React.useState(false);
  const [enrichResult, setEnrichResult] = React.useState<string | null>(null);
  const isStaged = isApproved || isRejected;  // staged but not yet submitted

  const cardCls = `relative rounded-xl border bg-card transition-all duration-200 ${
    effectiveApproved
      ? 'border-emerald-300 dark:border-emerald-700' + (isStaged ? ' ring-1 ring-emerald-300 dark:ring-emerald-700' : ' opacity-75')
      : effectiveRejected
      ? 'border-red-200 dark:border-red-900 opacity-50'
      : 'border-border hover:border-primary/30 hover:shadow-md'
  }`;

  return (
    <div className={cardCls}>
      {/* Status badge */}
      {effectiveApproved && (
        <div className="absolute top-3 right-3 flex items-center gap-1 px-2 py-0.5 rounded-full bg-emerald-100 dark:bg-emerald-950/40 text-emerald-700 dark:text-emerald-400 text-xs font-medium z-10">
          <CheckCircle className="h-3 w-3" />
          {isStaged ? 'Staged' : 'Approved'}
        </div>
      )}
      {effectiveRejected && (
        <div className="absolute top-3 right-3 flex items-center gap-1 px-2 py-0.5 rounded-full bg-red-100 dark:bg-red-950/40 text-red-700 dark:text-red-400 text-xs font-medium z-10">
          <XCircle className="h-3 w-3" />
          {isStaged ? 'Staged reject' : 'Rejected'}
        </div>
      )}

      {/* View in Accounts link — only after actual DB approval */}
      {p.reviewStatus === 'APPROVED' && p.createdAccountId && (
        <div className="absolute top-8 right-3 mt-1 z-10">
          <a
            href={`/accounts/${p.createdAccountId}`}
            className="text-xs text-primary hover:underline flex items-center gap-0.5"
          >
            View in Accounts <ExternalLink className="h-2.5 w-2.5" />
          </a>
        </div>
      )}

      <div className="p-4 space-y-3">
        {/* Header */}
        <div className="flex items-start gap-3 pr-24">
          <div className="flex-1 min-w-0">
            <div className="flex items-center gap-2">
              <h3 className="font-semibold text-sm text-foreground truncate">{p.companyName}</h3>
              {(p.accountType || 'BUYER').toUpperCase() === 'PARTNER' && (
                <span className="shrink-0 px-1.5 py-0.5 rounded-full text-[10px] font-semibold bg-rose-100 text-rose-700 dark:bg-rose-950/30 dark:text-rose-400">
                  Partner{p.partnerType ? ` · ${String(p.partnerType).replace(/_/g, ' ')}` : ''}
                </span>
              )}
            </div>
            <div className="flex items-center gap-2 mt-0.5 flex-wrap">
              {p.domain && (
                <a
                  href={`https://${p.domain}`}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-xs text-muted-foreground hover:text-primary flex items-center gap-0.5"
                >
                  <Globe className="h-3 w-3" />{p.domain}
                </a>
              )}
              {p.region && <span className="text-xs text-muted-foreground">{p.region}</span>}
              {p.estimatedSize && <span className="text-xs text-muted-foreground">{p.estimatedSize}</span>}
            </div>
          </div>
        </div>

        {/* Score row */}
        <div className="flex flex-wrap items-center gap-1.5">
          <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-bold ${gradeClass}`}>
            ICP {grade}{p.icpScoreEstimate !== null ? ` · ${p.icpScoreEstimate}` : ''}
          </span>
          {(p.accountType || 'BUYER').toUpperCase() === 'PARTNER' ? (
            <>
              {p.partnerTerritory && (
                <span className="px-2 py-0.5 rounded-full text-xs font-medium bg-rose-50 text-rose-700 dark:bg-rose-950/20 dark:text-rose-400">
                  {p.partnerTerritory}
                </span>
              )}
              {p.partnerMarketFocus && (
                <span className="px-2 py-0.5 rounded-full text-xs font-medium bg-muted text-muted-foreground">
                  {p.partnerMarketFocus}
                </span>
              )}
            </>
          ) : (
            <>
              {p.primaryPainAxis && (
                <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${PAIN_COLORS[p.primaryPainAxis] || 'bg-muted text-muted-foreground'}`}>
                  {p.primaryPainAxis}
                </span>
              )}
              {p.maturityBand && (
                <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${MATURITY_COLORS[p.maturityBand] || 'bg-muted text-muted-foreground'}`}>
                  {p.maturityBand}
                </span>
              )}
            </>
          )}
        </div>

        {/* Pain rationale (why this axis) */}
        {p.painRationale && (
          <div className="flex items-start gap-1.5 text-xs text-muted-foreground leading-relaxed">
            <Zap className="h-3.5 w-3.5 shrink-0 mt-0.5 text-amber-500" />
            <span>{p.painRationale}</span>
          </div>
        )}

        {/* Win theme */}
        {p.winTheme && (
          <blockquote className="border-l-2 border-primary/40 pl-3 text-xs text-muted-foreground italic leading-relaxed">
            {p.winTheme}
          </blockquote>
        )}

        {/* Cantier Systems use cases */}
        {p.cantieroCases && p.cantieroCases.length > 0 && (
          <div className="flex flex-wrap gap-1">
            {p.cantieroCases.slice(0, 2).map((c, i) => (
              <span key={i} className="flex items-center gap-0.5 px-2 py-0.5 rounded-md bg-primary/5 border border-primary/20 text-primary text-xs">
                <Zap className="h-2.5 w-2.5" />{c}
              </span>
            ))}
          </div>
        )}

        {/* Architecture tags */}
        {p.architectureTags && p.architectureTags.length > 0 && (
          <div className="flex flex-wrap gap-1">
            {p.architectureTags.slice(0, 4).map((t, i) => (
              <span key={i} className="flex items-center gap-0.5 px-1.5 py-0.5 rounded text-[10px] bg-muted text-muted-foreground font-mono">
                <Tag className="h-2 w-2" />{t}
              </span>
            ))}
          </div>
        )}

        {/* Buying signals */}
        {p.signals && p.signals.length > 0 && (
          <div className="flex flex-wrap gap-1.5">
            {p.signals.slice(0, 3).map((s, i) => (
              <span key={i} className="px-2 py-0.5 rounded-md bg-amber-50 dark:bg-amber-950/20 border border-amber-200 dark:border-amber-800 text-amber-700 dark:text-amber-400 text-xs">
                {s}
              </span>
            ))}
          </div>
        )}

        {/* Key contact */}
        {p.keyContactName && (
          <div className="flex items-center gap-2 text-xs">
            <div className="h-6 w-6 rounded-full bg-primary/10 flex items-center justify-center text-primary text-xs font-bold shrink-0">
              {p.keyContactName.charAt(0)}
            </div>
            <div className="flex-1 min-w-0">
              <span className="font-medium text-foreground">{p.keyContactName}</span>
              {p.keyContactTitle && <span className="text-muted-foreground"> · {p.keyContactTitle}</span>}
              {p.keyContactDept && <span className="text-muted-foreground"> · {p.keyContactDept}</span>}
            </div>
            {p.keyContactLinkedIn && (
              <a href={p.keyContactLinkedIn} target="_blank" rel="noopener noreferrer" className="text-blue-500 hover:text-blue-600 shrink-0">
                <Linkedin className="h-3.5 w-3.5" />
              </a>
            )}
          </div>
        )}

        {/* Additional contacts (merged from multiple rows for the same company) */}
        {Array.isArray(p.additionalContacts) && p.additionalContacts.length > 0 && (
          <div className="pl-8 space-y-1">
            {p.additionalContacts.map((c, i) => (
              <div key={i} className="flex items-center gap-2 text-xs text-muted-foreground">
                <span className="font-medium text-foreground/80">{c.name || c.title || 'Contact'}</span>
                {c.title && <span> · {c.title}</span>}
                {c.dept && <span> · {c.dept}</span>}
                {c.linkedin && (
                  <a href={c.linkedin} target="_blank" rel="noopener noreferrer" className="text-blue-500 hover:text-blue-600 shrink-0">
                    <Linkedin className="h-3 w-3" />
                  </a>
                )}
              </div>
            ))}
            <p className="text-[10px] text-muted-foreground/70 uppercase tracking-wide">
              {p.additionalContacts.length + 1} contacts · all created on approve
            </p>
          </div>
        )}

        {/* Outreach accordion */}
        {(p.outreachEmailSubj || p.outreachCallScript || p.outreachLinkedInDm) && (
          <div className="border border-border rounded-lg overflow-hidden">
            <button
              onClick={() => setOutreachOpen(!outreachOpen)}
              className="w-full flex items-center justify-between px-3 py-2 text-xs font-medium text-muted-foreground hover:bg-muted/50 transition-colors"
            >
              <span className="flex items-center gap-1.5"><Mail className="h-3.5 w-3.5" />Outreach Kit</span>
              {outreachOpen ? <ChevronUp className="h-3.5 w-3.5" /> : <ChevronDown className="h-3.5 w-3.5" />}
            </button>
            {outreachOpen && (
              <div className="border-t border-border">
                {/* Tab bar */}
                <div className="flex border-b border-border">
                  {(['email', 'call', 'linkedin'] as const).map(tab => (
                    <button
                      key={tab}
                      onClick={() => setOutreachTab(tab)}
                      className={`flex-1 py-1.5 text-xs font-medium transition-colors ${
                        outreachTab === tab
                          ? 'bg-primary/10 text-primary border-b-2 border-primary'
                          : 'text-muted-foreground hover:text-foreground'
                      }`}
                    >
                      {tab === 'email'    ? <><Mail    className="h-3 w-3 inline mr-1" />Email</>    :
                       tab === 'call'     ? <><Phone   className="h-3 w-3 inline mr-1" />Call</>     :
                       <><Linkedin className="h-3 w-3 inline mr-1" />LinkedIn</>}
                    </button>
                  ))}
                </div>
                {/* Tab content */}
                <div className="p-3 space-y-2">
                  {outreachTab === 'email' && (
                    <>
                      {p.outreachEmailSubj && (
                        <div>
                          <p className="text-xs font-medium text-muted-foreground mb-0.5">Subject</p>
                          <p className="text-xs text-foreground font-medium">{p.outreachEmailSubj}</p>
                        </div>
                      )}
                      {p.outreachEmailBody && (
                        <div>
                          <p className="text-xs font-medium text-muted-foreground mb-0.5">Body</p>
                          <p className="text-xs text-muted-foreground whitespace-pre-wrap leading-relaxed">{p.outreachEmailBody}</p>
                        </div>
                      )}
                    </>
                  )}
                  {outreachTab === 'call' && p.outreachCallScript && (
                    <p className="text-xs text-muted-foreground whitespace-pre-wrap leading-relaxed">{p.outreachCallScript}</p>
                  )}
                  {outreachTab === 'linkedin' && p.outreachLinkedInDm && (
                    <p className="text-xs text-muted-foreground whitespace-pre-wrap leading-relaxed">{p.outreachLinkedInDm}</p>
                  )}
                </div>
              </div>
            )}
          </div>
        )}

        {/* Source URLs */}
        {p.sourceUrls && p.sourceUrls.length > 0 && (
          <div>
            <button
              onClick={() => setSourcesOpen(!sourcesOpen)}
              className="text-xs text-muted-foreground hover:text-primary flex items-center gap-1 transition-colors"
            >
              {p.sourceUrls.length} source{p.sourceUrls.length !== 1 ? 's' : ''} verified
              {sourcesOpen ? <ChevronUp className="h-3 w-3" /> : <ChevronDown className="h-3 w-3" />}
            </button>
            {sourcesOpen && (
              <div className="mt-1 space-y-0.5">
                {p.sourceUrls.map((url, i) => (
                  <a key={i} href={url} target="_blank" rel="noopener noreferrer"
                    className="block text-xs text-primary hover:underline truncate">
                    {url}
                  </a>
                ))}
              </div>
            )}
          </div>
        )}

        {/* Contact warnings */}
        {!isDone && needsEmail && (
          <div className="flex items-start gap-1.5 px-3 py-2 rounded-lg bg-red-50 dark:bg-red-950/20 border border-red-200 dark:border-red-800 text-xs text-red-700 dark:text-red-400 mb-1">
            <AlertTriangle className="h-3.5 w-3.5 shrink-0 mt-0.5" />
            <span>No contact found — click Edit to add a contact name before approving.</span>
          </div>
        )}
        {!isDone && missingEmail && (
          <div className="flex items-start justify-between gap-2 px-3 py-2 rounded-lg bg-amber-50 dark:bg-amber-950/20 border border-amber-200 dark:border-amber-800 text-xs text-amber-700 dark:text-amber-400 mb-1">
            <div className="flex items-start gap-1.5">
              <AlertTriangle className="h-3.5 w-3.5 shrink-0 mt-0.5" />
              <span>
                {enrichResult || (
                  p.domain
                    ? 'No email — configure Lusha or Hunter.io, then click Find.'
                    : 'No domain or email — click Edit to add company website, then Find.'
                )}
              </span>
            </div>
            <button
              onClick={async () => {
                setEnriching(true);
                setEnrichResult(null);
                try {
                  const result = await enrichContact(p.id);
                  setEnrichResult(result.found
                    ? `✓ Found via ${result.tried?.join(', ')}`
                    : result.message || 'No email found');
                  if (result.found && onEnrich) {
                    onEnrich({
                      keyContactEmail:    result.keyContactEmail,
                      keyContactPhone:    result.keyContactPhone,
                      keyContactName:     result.keyContactName,
                      keyContactLinkedIn: result.keyContactLinkedIn,
                    });
                  }
                } catch (e: any) {
                  const err = e?.response?.data;
                  if (err?.hint === 'domain_required') {
                    setEnrichResult('Add company domain via Edit first, then click Find.');
                  } else {
                    setEnrichResult(err?.error || 'Connector not configured — add Lusha or Hunter.io key.');
                  }
                } finally {
                  setEnriching(false);
                }
              }}
              disabled={enriching}
              className="flex-shrink-0 flex items-center gap-1 px-2 py-1 rounded bg-amber-100 dark:bg-amber-900/30 hover:bg-amber-200 dark:hover:bg-amber-900/50 text-amber-700 dark:text-amber-400 font-medium transition-colors disabled:opacity-50"
            >
              {enriching
                ? <Loader2 className="h-3 w-3 animate-spin" />
                : <Search className="h-3 w-3" />}
              {enriching ? 'Searching...' : 'Find'}
            </button>
          </div>
        )}

        {/* Action buttons */}
        {!isDone && (
          <div className="flex items-center gap-2 pt-1 border-t border-border">
            <button
              onClick={onApprove}
              className="flex-1 flex items-center justify-center gap-1 py-1.5 rounded-lg bg-emerald-600 hover:bg-emerald-700 text-white text-xs font-medium transition-colors"
            >
              <CheckCircle className="h-3.5 w-3.5" />Approve
            </button>
            <button
              onClick={onReject}
              className="flex-1 flex items-center justify-center gap-1 py-1.5 rounded-lg bg-red-50 hover:bg-red-100 dark:bg-red-950/20 dark:hover:bg-red-950/40 text-red-600 dark:text-red-400 border border-red-200 dark:border-red-800 text-xs font-medium transition-colors"
            >
              <XCircle className="h-3.5 w-3.5" />Reject
            </button>
            <button
              onClick={onEdit}
              className="px-3 py-1.5 rounded-lg bg-muted hover:bg-muted/80 text-muted-foreground border border-border text-xs font-medium transition-colors"
              title="Edit before approving"
            >
              <Edit2 className="h-3.5 w-3.5" />
            </button>
          </div>
        )}

        {/* Unstage button for staged items */}
        {isStaged && (
          <button
            onClick={onApprove}  // toggles back
            className="w-full py-1 text-xs text-muted-foreground hover:text-foreground transition-colors"
          >
            ↩ Undo
          </button>
        )}
      </div>
    </div>
  );
}
